import React, { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Package, Calendar, AlertTriangle, Clock, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
}
function daysFromNow(s) {
  if (!s) return null;
  return Math.ceil((new Date(s).getTime() - Date.now()) / 86400000);
}

const STATUS_META = {
  active:   { label: "On loan",  text: "text-emerald-600 dark:text-emerald-300", bg: "bg-emerald-500/10",  border: "border-emerald-500/20", icon: Clock },
  overdue:  { label: "Overdue",  text: "text-red-600 dark:text-red-300",         bg: "bg-red-500/10",      border: "border-red-500/20",     icon: AlertTriangle },
};


export default function MemberLoansPage() {
  const [loans, setLoans] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/equipment-loans/mine");
        setLoans(data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load loans");
        setLoans([]);
      }
    })();
  }, []);

  // Once a member returns a loan, it is removed from their portal entirely.
  // Admins still see the full audit trail in the Equipment Loans admin panel.
  const active = (loans || []).filter((x) => x.status !== "returned");
  const overdueAny = active.some((x) => x.status === "overdue");

  return (
    <AppShell variant="member" title="My equipment loans">
      <div className="space-y-6 max-w-3xl">
        {/* Header card */}
        <Card className="p-5 rounded-2xl flex items-start gap-4" data-testid="member-loans-header">
          <div className="h-12 w-12 rounded-2xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] flex items-center justify-center">
            <Package className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">On Loan</h2>
            <p className="text-xs text-muted-foreground">
              Items the club has lent to you. Please return them by the due date or contact the Property Officer to extend the loan.
            </p>
          </div>
          {overdueAny && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-500/10 text-red-600 dark:text-red-300 border border-red-500/20">
              <AlertTriangle className="h-3.5 w-3.5" />
              Action needed
            </span>
          )}
        </Card>

        {loans === null && (
          <div className="space-y-3">
            <Skeleton className="h-24 rounded-2xl" />
            <Skeleton className="h-24 rounded-2xl" />
          </div>
        )}

        {loans !== null && active.length === 0 && (
          <Card className="p-10 rounded-2xl text-center" data-testid="member-loans-empty">
            <Package className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium">No equipment on loan</p>
            <p className="text-xs text-muted-foreground mt-1">You&rsquo;ll see items here when the club issues you any gear.</p>
          </Card>
        )}

        {active.length > 0 && (
          <div className="space-y-3" data-testid="member-loans-active-list">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold px-1">Active</div>
            {active.map((ln) => <LoanCard key={ln.id} loan={ln} />)}
          </div>
        )}
      </div>
    </AppShell>
  );
}


function LoanCard({ loan }) {
  const meta = STATUS_META[loan.status] || STATUS_META.active;
  const Icon = meta.icon;
  const days = daysFromNow(loan.due_date);
  const isOverdue = loan.status === "overdue";
  return (
    <Card className={`p-5 rounded-2xl border ${isOverdue ? "border-red-500/30" : ""}`} data-testid={`member-loan-${loan.id}`}>
      <div className="flex items-start gap-3">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.bg} ${meta.text} ${meta.border} border`}>
          <Icon className="h-3 w-3" />
          {meta.label}
        </span>
        <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          Due {fmtDate(loan.due_date)}
          {loan.status === "active" && days !== null && days >= 0 && (
            <span className="ml-1">(in {days} day{days === 1 ? "" : "s"})</span>
          )}
          {loan.status === "overdue" && days !== null && (
            <span className="ml-1 text-red-500 font-medium">({Math.abs(days)} day{Math.abs(days) === 1 ? "" : "s"} overdue)</span>
          )}
        </div>
      </div>
      <div className="mt-3 text-sm font-medium whitespace-pre-wrap">{loan.items}</div>
      {loan.notes && <div className="mt-1 text-[12px] italic text-muted-foreground">{loan.notes}</div>}
      <div className="mt-3 grid sm:grid-cols-3 gap-2 text-[11px] text-muted-foreground">
        <div>Loaned {fmtDate(loan.loaned_at)}</div>
        {loan.last_extended_at && (
          <div className="inline-flex items-center gap-1">
            <RotateCcw className="h-3 w-3" /> Extended {fmtDate(loan.last_extended_at)}
          </div>
        )}
      </div>
      {isOverdue && (
        <div className="mt-4 px-3 py-2 rounded-lg bg-red-500/8 text-red-700 dark:text-red-300 text-xs border border-red-500/20">
          These items are overdue. Please return them or contact the Property Officer to extend the loan.
        </div>
      )}
    </Card>
  );
}
